import { useMemo } from 'react'
import { RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ROW, TD, TH, relativeTime } from '@/features/fraud/format'
import { Spinner } from '@/features/fraud/shared'
import { discountErrorMessage } from '@/lib/api/discount-client'
import { useDiscountReport } from '@/lib/api/discount-queries'
import type { DiscountReportRow, DiscountReportTotals } from '@/lib/api/discount-types'
import { useEvent } from '@/lib/api/queries'
import { cn } from '@/lib/utils'
import { formatCurrency } from './money'

type Side = 'token' | 'currency'

const NUM_TH = cn(TH, 'text-right')
const NUM_TD = cn(TD, 'text-right tabular-nums whitespace-nowrap')
const tokenNumber = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 2 })

function hasSales(totals: DiscountReportTotals): boolean {
  return totals.units > 0 || totals.list_value !== 0 || totals.charged_value !== 0
}

function sum(rows: DiscountReportRow[], side: Side): DiscountReportTotals {
  return rows.reduce<DiscountReportTotals>(
    (acc, row) => ({
      units: acc.units + row[side].units,
      list_value: acc.list_value + row[side].list_value,
      charged_value: acc.charged_value + row[side].charged_value,
      amount_discounted: acc.amount_discounted + row[side].amount_discounted,
    }),
    { units: 0, list_value: 0, charged_value: 0, amount_discounted: 0 },
  )
}

function percentOff(totals: DiscountReportTotals): string | null {
  if (!totals.list_value) return null
  return `${Math.round((totals.amount_discounted / totals.list_value) * 100)}%`
}

function ScheduleName({ row }: { row: DiscountReportRow }) {
  return (
    <>
      <span className="font-medium text-neutral-900 dark:text-white">
        {row.name || (row.schedule_id ? `Schedule #${row.schedule_id}` : 'Unnamed schedule')}
      </span>
      {row.schedule_id == null && <span className="ml-1.5 text-xs text-neutral-400">(removed)</span>}
    </>
  )
}

function Section({ title, unit, rows, side, format }: {
  title: string
  unit: string
  rows: DiscountReportRow[]
  side: Side
  format: (value: number) => string
}) {
  const sorted = [...rows].sort((a, b) => b[side].amount_discounted - a[side].amount_discounted)
  const total = sum(rows, side)
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">Values in {unit}.</p>
      </CardHeader>
      <CardContent className="overflow-x-auto px-0">
        <table className="w-full whitespace-nowrap">
          <thead>
            <tr className="border-b border-neutral-200 dark:border-neutral-800">
              <th className={cn(TH, 'pl-6')}>Schedule</th>
              <th className={NUM_TH}>Units</th>
              <th className={NUM_TH}>List value</th>
              <th className={NUM_TH}>Charged</th>
              <th className={cn(NUM_TH, 'pr-6')}>Discounted</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const totals = row[side]
              const pct = percentOff(totals)
              return (
                <tr key={`${row.schedule_id ?? 'none'}-${row.name}`} className={ROW}>
                  <td className={cn(TD, 'pl-6 whitespace-normal')}><ScheduleName row={row} /></td>
                  <td className={NUM_TD}>{totals.units.toLocaleString('en-CA')}</td>
                  <td className={NUM_TD}>{format(totals.list_value)}</td>
                  <td className={NUM_TD}>{format(totals.charged_value)}</td>
                  <td className={cn(NUM_TD, 'pr-6 font-medium text-neutral-900 dark:text-white')}>
                    {format(totals.amount_discounted)}
                    {pct && <span className="ml-1.5 text-xs font-normal text-neutral-500 dark:text-neutral-400">{pct}</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50">
              <td className={cn(TD, 'pl-6 font-semibold text-neutral-900 dark:text-white')}>Total</td>
              <td className={cn(NUM_TD, 'font-semibold')}>{total.units.toLocaleString('en-CA')}</td>
              <td className={cn(NUM_TD, 'font-semibold')}>{format(total.list_value)}</td>
              <td className={cn(NUM_TD, 'font-semibold')}>{format(total.charged_value)}</td>
              <td className={cn(NUM_TD, 'pr-6 font-semibold text-neutral-900 dark:text-white')}>
                {format(total.amount_discounted)}
                {percentOff(total) && (
                  <span className="ml-1.5 text-xs font-normal text-neutral-500 dark:text-neutral-400">{percentOff(total)}</span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </CardContent>
    </Card>
  )
}

/** What each schedule has taken off, split by how the lines were paid. */
export function ReportTab({ eventId }: { eventId: number }) {
  const { data: event } = useEvent(eventId)
  const { data, isLoading, isFetching, error, dataUpdatedAt, refetch } = useDiscountReport(eventId)
  const currencyCode = (event?.currency || 'CAD').toUpperCase()

  const { tokenRows, currencyRows } = useMemo(() => {
    const rows = data?.schedules ?? []
    return {
      tokenRows: rows.filter((row) => hasSales(row.token)),
      currencyRows: rows.filter((row) => hasSales(row.currency)),
    }
  }, [data])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Only charged orders count; refunded lines are excluded.
        </p>
        <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
          {dataUpdatedAt ? <span>Updated {relativeTime(new Date(dataUpdatedAt).toISOString())}</span> : null}
          <button onClick={() => refetch()} className="inline-flex items-center gap-1 hover:text-neutral-900 dark:hover:text-white" disabled={isFetching}>
            <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} /> Refresh
          </button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">Failed to load the promotions report: {discountErrorMessage(error)}</p>
      ) : isLoading || !data ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : tokenRows.length === 0 && currencyRows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 px-4 py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">
          No discounted sales yet
        </div>
      ) : (
        <>
          {tokenRows.length > 0 && (
            <Section
              title="Tokens"
              unit="tokens"
              rows={tokenRows}
              side="token"
              format={(value) => tokenNumber.format(value)}
            />
          )}
          {currencyRows.length > 0 && (
            <Section
              title="Card / Direct Pay"
              unit={currencyCode}
              rows={currencyRows}
              side="currency"
              format={(value) => formatCurrency(value, currencyCode)}
            />
          )}
        </>
      )}
    </div>
  )
}
